import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

interface BusinessLabels {
  search: string;
  logistics: string;
  forwarding: string;
  payment: string;
}

interface PersonalLabels {
  course: string;
  telegram: string;
  rates: string;
}

interface BreadcrumbLabels {
  about: string;
  blog: string;
  contacts: string;
  services: string;
  prices: string;
  'order-delivery': string;
  business: BusinessLabels;
  personal: PersonalLabels;
}

const breadcrumbLabels: BreadcrumbLabels = {
  about: 'О нас',
  blog: 'Блог',
  contacts: 'Контакты',
  services: 'Услуги',
  prices: 'Цены',
  'order-delivery': 'Заказать доставку',
  business: {
    search: 'Поиск поставщиков',
    logistics: 'Логистика',
    forwarding: 'Экспедирование',
    payment: 'Оплата поставщикам'
  },
  personal: {
    course: 'Обучающий курс',
    telegram: 'Telegram бот',
    rates: 'Тарифы'
  }
};

function Breadcrumbs() {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  const getBreadcrumbLabel = (path: string, index: number): string => {
    // Handle business routes
    if (index > 0 && pathnames[index - 1] === 'business') {
      const businessLabel = (breadcrumbLabels.business as Record<string, string>)[path];
      return businessLabel || path;
    }
    
    // Handle personal routes
    if (index > 0 && pathnames[index - 1] === 'personal') {
      const personalLabel = (breadcrumbLabels.personal as Record<string, string>)[path];
      return personalLabel || path;
    }

    // Handle top-level routes
    if (typeof breadcrumbLabels[path as keyof BreadcrumbLabels] === 'string') {
      return breadcrumbLabels[path as keyof BreadcrumbLabels] as string;
    }

    // If no match is found, return the path itself
    return path;
  };

  return (
    <div className="overflow-x-auto pb-2">
      <nav className={`flex items-center gap-2 text-sm whitespace-nowrap ${
        location.pathname.includes('/services/') ? 'text-white/80' : 'text-white/80'
      }`}>
        <a 
          href="/" 
          className={`transition-colors ${
            location.pathname.includes('/services/') ? 'hover:text-white' : 'hover:text-white'
          }`}
        >
          Главная
        </a>
        {pathnames.map((name, index) => {
          const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
          const isLast = index === pathnames.length - 1;
          let label;
          
          // Handle business section specially
          if (name === 'business') {
            label = 'Для бизнеса';
            return (
              <React.Fragment key={name}>
                <ChevronRight className="w-4 h-4 flex-shrink-0" />
                <a
                  href="/services"
                  className="hover:text-white transition-colors"
                >
                  {label}
                </a>
              </React.Fragment>
            );
          }
          
          label = getBreadcrumbLabel(name, index);

          return (
            <React.Fragment key={name}>
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
              {isLast ? (
                <span className={`font-medium ${
                  location.pathname.includes('/services/') ? 'text-white' : 'text-white'
                }`}>{label}</span>
              ) : (
                <a
                  href={routeTo}
                  className={`transition-colors ${
                    location.pathname.includes('/services/') ? 'hover:text-white' : 'hover:text-white'
                  }`}
                >
                  {label}
                </a>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    </div>
  );
}

export default Breadcrumbs;